import React from "react";
import { Link } from "react-router-dom";
import Countdown from "@/components/Countdown";
import Seo, { eventJsonLd, howToJsonLd, faqJsonLd } from "@/components/Seo";
import { Trophy, Ticket, QrCode, Gift, Clock, ShieldCheck } from "lucide-react";

const FANFEST_FAQ = [
  { q: "What is ADN Local FanFest?",
    a: "ADN Local FanFest is a free citywide watch-party sweepstakes running from July 6 to July 19, 2026 in Jacksonville, Florida, culminating in a live Grand Prize drawing after the FIFA World Cup Final." },
  { q: "Who is eligible to enter?",
    a: "Anyone 18 years of age or older, regardless of where they are from. One entry per family/household. No purchase necessary." },
  { q: "What are the daily prizes?",
    a: "$100 restaurant certificates, Jacksonville hotel weekend stays, travel and experience certificates, and $50 sponsor gift cards. 13 days of prizes from July 6 through July 18." },
  { q: "Where do I check in?",
    a: "At any Official Watch Party Partner location — bars, breweries, restaurants, and hotels across Jacksonville participating in the campaign. Locations are listed at /directory." },
  { q: "Do I have to be present to win?",
    a: "You do NOT have to be present to win daily prizes. However, the Grand Prize requires you to be checked in via the ADN app at a Watch Party Partner between 12:00 PM and 2:45 PM EDT on July 19, 2026." },
];

export default function FanFest() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <Seo
        title="FanFest 2026 · Jacksonville World Cup Watch Party"
        description="Full campaign guide for ADN Local FanFest 2026. Grand Prize, daily prizes, how check-ins work, and eligibility for the Cancun trip drawing on July 19, 2026."
        type="event"
        jsonLd={[eventJsonLd(), howToJsonLd(), faqJsonLd(FANFEST_FAQ)]}
      />
      <div className="text-center">
        <div className="text-xs tracking-[0.3em] uppercase text-primary">Campaign Overview</div>
        <h1 className="font-bebas text-5xl sm:text-6xl mt-3">ADN LOCAL™ FANFEST™</h1>
        <p className="text-white/70 mt-2 max-w-2xl mx-auto">
          Jacksonville's citywide watch-party experience powered by local businesses. Free to enter. Real prizes. Zero purchase required.
        </p>
        <div className="mt-8"><Countdown /></div>
      </div>

      {/* Prize Sections */}
      <div className="grid md:grid-cols-2 gap-6 mt-14">
        <div className="grand-card p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase purple-text">Grand Prize</div>
          <h2 className="font-bebas text-4xl gold-text mt-2">CANCUN 6D/5N + $500 CASH</h2>
          <p className="mt-3 text-white/80">6 days / 5 nights in Cancun for two, roundtrip airfare included ($800/person), plus $500 cash for the winner. Winner pays only nightly taxes and resort fees. ARV $2,999. Drawn live after the July 19 World Cup Final from a surprise Jacksonville location.</p>
          <div className="mt-4 flex items-center gap-2 text-sm text-white/70"><Clock size={14}/> Check-in window: July 19, 12:00 – 2:45 PM EDT</div>
        </div>
        <div className="adn-card p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-primary">Daily Prizes · July 6–18</div>
          <h2 className="font-bebas text-4xl mt-2">13 DAYS OF WINS</h2>
          <ul className="mt-3 space-y-1 text-white/80 list-disc list-inside">
            <li>$100 Restaurant certificates</li>
            <li>Jacksonville Hotel weekend stays</li>
            <li>Travel & experience certificates</li>
            <li>$50 sponsor gift cards</li>
          </ul>
          <div className="mt-4 text-xs text-white/50">You do not have to be present to win daily prizes.</div>
        </div>
      </div>

      {/* How to register / check-in */}
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {[
          { icon: Ticket, title: "HOW TO REGISTER", body: "Fill out the free entry form. Accept the Official Rules and Privacy Policy. Confirm one entry per family/household. Age 18+." },
          { icon: QrCode, title: "HOW CHECK-INS WORK", body: "At any Watch Party Partner™, scan the ADN QR code with your phone. GPS validates. Your Fan Passport™ stamp is instant." },
          { icon: ShieldCheck, title: "GRAND PRIZE ELIGIBILITY", body: "On July 19, 12:00 – 2:45 PM EDT, check in at any Watch Party Partner™. Match starts 3 PM. Draw follows live." },
        ].map((s,i)=>(
          <div key={i} className="adn-card p-6">
            <s.icon className="text-primary" />
            <h3 className="font-bebas text-2xl mt-3">{s.title}</h3>
            <p className="text-white/70 text-sm mt-2 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-14 text-center">
        <Link to="/register" data-testid="fanfest-register-cta"
          className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-black font-bebas text-2xl rounded-sm hover:brightness-110 min-h-[56px]">
          ENTER FREE <Gift size={18}/>
        </Link>
        <div className="mt-3 text-xs text-white/50">
          <Link to="/rules" className="underline">Official Rules</Link> ·{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
